<script setup lang="ts">
import type { ProjectTeamMember, ProjectGalleryItem } from '~/composables/useProjects'

defineProps<{
  project: {
    formation?: string
    isAIUsed?: boolean
    aiDetails?: string
    duration?: string
    promotionName?: string
    realizationDate?: string | null
    tags?: string[]
    links?: { url: string; platform: string; label?: string }[]
  }
  galleryDocs?: ProjectGalleryItem[]
}>()
</script>

<template>
  <section class="bg-oxo-card-bg/60 backdrop-blur-3xl rounded-[2.5rem] border border-oxo-border p-8 md:p-10 space-y-8 shadow-2xl transition-colors hover:border-oxo-red/20" aria-label="Spécifications techniques">
    <header class="space-y-2">
      <h3 class="text-[10px] font-black uppercase tracking-[0.3em] text-oxo-red">Spécifications</h3>
      <h4 class="font-display font-black text-2xl uppercase text-foreground">Fiche Technique</h4>
    </header>
    
    <div class="grid grid-cols-1 gap-8">
      <dl class="grid grid-cols-2 gap-y-6 gap-x-4">
         <div class="space-y-1">
            <dt class="text-foreground-dim/40 text-[10px] font-black uppercase tracking-widest block">Formation</dt>
            <dd class="text-xs font-bold text-foreground">{{ project.formation || 'N/A' }}</dd>
         </div>
         <div class="space-y-1">
            <dt class="text-foreground-dim/40 text-[10px] font-black uppercase tracking-widest block">Transparence IA</dt>
            <dd class="flex items-center gap-1.5 text-xs font-bold" :class="project.isAIUsed ? 'text-oxo-red' : 'text-foreground-dim/60'">
              <i :class="['fas', project.isAIUsed ? 'fa-robot' : 'fa-user-check', 'text-[10px]']" aria-hidden="true"></i>
              {{ project.isAIUsed ? 'IA Utilisée' : 'Sans IA' }}
            </dd>
          </div>
         <div class="space-y-1">
            <dt class="text-white/40 text-[10px] font-black uppercase tracking-widest block">Statut</dt>
            <dd class="flex items-center gap-1.5 text-xs font-bold text-green-400">
              <span class="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
              Terminé
            </dd>
         </div>
         <div class="space-y-1">
            <dt class="text-foreground-dim/40 text-[10px] font-black uppercase tracking-widest block">Promotion</dt>
            <dd class="text-xs font-bold leading-tight text-foreground">
              {{ project.promotionName || 'Promotion' }}
            </dd>
         </div>
         <div class="space-y-1">
            <dt class="text-foreground-dim/40 text-[10px] font-black uppercase tracking-widest block">Date de réalisation</dt>
            <dd class="text-xs font-bold leading-tight text-foreground">
              <template v-if="project.realizationDate">
                <span class="capitalize">
                  {{ new Date(project.realizationDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) }}
                </span>
              </template>
            </dd>
         </div>
         <div class="space-y-1">
            <dt class="text-foreground-dim/40 text-[10px] font-black uppercase tracking-widest block">Temps de réalisation</dt>
            <dd class="text-xs font-bold text-foreground">{{ project.duration || 'N/A' }}</dd>
         </div>
      </dl>

      <section v-if="project.tags?.length" class="space-y-3" aria-label="Technologies utilisées">
        <header class="flex items-center gap-2 text-foreground-dim/40 uppercase text-[10px] font-black tracking-widest">
          <i class="fas fa-tags text-oxo-red" aria-hidden="true"></i>
          Technologies
        </header>
        <ul class="flex flex-wrap gap-2 list-none p-0">
          <li v-for="tag in project.tags" :key="tag" class="px-3 py-1 bg-background border border-oxo-border text-[10px] rounded-full font-bold text-foreground-dim">
            {{ tag }}
          </li>
        </ul>
      </section>
      
      <section v-if="project.isAIUsed && project.aiDetails" class="space-y-3 pt-6 border-t border-oxo-border" aria-label="Usage de l'intelligence artificielle">
        <header class="flex items-center gap-2 text-foreground-dim/40 uppercase text-[10px] font-black tracking-widest">
          <i class="fas fa-robot text-oxo-red" aria-hidden="true"></i>
          Usage de l'IA
        </header>
        <p class="text-[11px] text-foreground-dim leading-relaxed">
          {{ project.aiDetails }}
        </p>
      </section>

      <nav v-if="galleryDocs && galleryDocs.length > 0" class="pt-6 border-t border-oxo-border space-y-3" aria-label="Documents annexes">
         <div class="text-foreground-dim/40 text-[10px] font-black uppercase tracking-widest mb-2">Documents Annexes</div>
         <ul class="space-y-3 list-none p-0">
            <li v-for="(doc, idx) in galleryDocs" :key="idx">
               <a 
                :href="doc.url" 
                target="_blank"
                class="flex items-center justify-between p-3 bg-background border border-oxo-border hover:bg-oxo-card-bg/50 rounded-xl transition-all group"
               >
                 <div class="flex items-center gap-3">
                    <i class="far fa-file-pdf text-oxo-red" aria-hidden="true"></i>
                    <span class="text-[10px] font-bold truncate max-w-[150px] text-foreground">{{ doc.description || doc.url.split('/').pop() || 'Document' }}</span>
                 </div>
                 <i class="fas fa-download text-[10px] text-foreground-dim/40 group-hover:text-foreground" aria-hidden="true"></i>
               </a>
            </li>
         </ul>
      </nav>

      <nav v-if="(project.links?.length ?? 0) > 0" class="pt-8 border-t border-oxo-border space-y-4" aria-label="Actions du projet">
         <div class="text-foreground-dim/40 text-[10px] font-black uppercase tracking-widest mb-2">Jouer & Découvrir</div>
         <ul class="space-y-4 list-none p-0">
            <li v-for="link in project.links" :key="link.url">
               <a 
                :href="link.url" 
                target="_blank"
                class="group relative flex flex-col gap-1 p-5 bg-background border border-oxo-border hover:border-oxo-red/50 rounded-2xl transition-all duration-500 overflow-hidden"
               >
                 <div class="absolute top-0 right-0 w-32 h-32 bg-oxo-red/5 translate-x-16 -translate-y-16 rounded-full blur-3xl group-hover:bg-oxo-red/10 transition-colors"></div>
                 
                 <div class="flex items-center justify-between relative z-10">
                    <div class="flex flex-col">
                       <span class="text-xs font-black uppercase tracking-widest text-white group-hover:text-oxo-red transition-colors">
                          {{ link.label || link.platform }}
                       </span>
                       <span v-if="link.label" class="text-[10px] font-bold text-foreground-dim/60 uppercase">
                          Via {{ link.platform }}
                       </span>
                    </div>
                    <div class="w-10 h-10 rounded-xl bg-oxo-card-bg border border-oxo-border flex items-center justify-center group-hover:bg-oxo-red group-hover:border-oxo-red group-hover:text-white transition-all duration-500 shadow-lg group-hover:shadow-oxo-red/20 group-hover:-translate-y-0.5">
                       <i v-if="link.platform.toLowerCase().includes('steam')" class="fab fa-steam text-sm" aria-hidden="true"></i>
                       <i v-else-if="link.platform.toLowerCase().includes('itch')" class="fab fa-itch-io text-sm" aria-hidden="true"></i>
                       <i v-else-if="link.platform.toLowerCase().includes('github')" class="fab fa-github text-sm" aria-hidden="true"></i>
                       <i v-else-if="link.platform.toLowerCase().includes('web') || link.platform.toLowerCase().includes('site')" class="fas fa-globe text-sm" aria-hidden="true"></i>
                       <i v-else class="fas fa-external-link-alt text-[10px]" aria-hidden="true"></i>
                    </div>
                 </div>
                 <div class="absolute bottom-0 left-0 h-0.5 bg-oxo-red transition-all duration-500 w-0 group-hover:w-full"></div>
               </a>
            </li>
         </ul>
      </nav>
    </div>
  </section>
</template>
